import React, {useEffect, useState} from 'react';
import {
    Box, Button, Skeleton,
    Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography, Slide
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import {useTheme} from '@mui/material/styles';
import {AuditFsAnnualService} from '@/services/auditService';
import {AnnualFsStatusEnum} from '@/services/auditService';
import {InputField, SnackbarField} from '@/components/common';
import {FsDetail, FsCreateStepper} from '@/pages/components/audit/finance-statement';

export function FsList() {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<any[]>([]);
    // pagination
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [total, setTotal] = useState<number>(0);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error';
    }>({open: false});
    const [createOpen, setCreateOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRow, setDetailRow] = useState<any | null>(null);
    const [filterCompanyName, setFilterCompanyName] = useState('');
    const [filterYearOfAssessment, setFilterYearOfAssessment] = useState('');
    const [expandedCompany, setExpandedCompany] = useState<string | null>(null);

    /**
     * 拉取 FS 列表。
     * @param opts.silent true=无感刷新（保留当前行，不打龙骨）；false/缺省=整表 Skeleton（菜单进入、筛选、翻页）
     */
    const load = async (opts?: {silent?: boolean}) => {
        const silent = !!opts?.silent;
        // WHY: Detail 返回/创建成功时列表已在屏上，避免 Skeleton 闪屏
        if (!silent) {
            setLoading(true);
        }
        try {
            const resp = await AuditFsAnnualService.queryPage(AnnualFsStatusEnum.AUDIT_EDITING, {
                pageNumber: pageNum,
                pageSize,
                pageType: 1,
                companyName: filterCompanyName || undefined,
                yearOfAssessment: filterYearOfAssessment || undefined,
            });
            const list = (resp?.list || []) as any[];
            setRows(list);
            setTotal(Number(resp?.totalRow || 0));
            setError(null);
        } catch (e: any) {
            setError(e?.message || 'Load failed');
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    // 菜单进入 / 筛选：龙骨
    useEffect(() => {
        const handler = setTimeout(() => {
            void load();
        }, 400);
        return () => clearTimeout(handler);
    }, [filterCompanyName, filterYearOfAssessment]);

    // 翻页：龙骨
    useEffect(() => {
        void load();
    }, [pageNum, pageSize]);

    const dialogs = (
        <>
            {/* Create: handled within FsCreateStepper */}
            <FsCreateStepper
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onCreated={(message: string | undefined) => {
                    setCreateOpen(false);
                    setSnackbar({open: true, message: message || 'Created successfully', severity: 'success'});
                    void load({silent: true});
                }}
                onOpenDetail={({companyId, annualId, companyName}) => {
                    setCreateOpen(false);
                    setDetailRow({companyId, annualId, companyName: companyName ?? null});
                    setDetailOpen(true);
                }}
            />

            {/* Details: handled within FsDetail */}
            {detailRow && (
                <FsDetail
                    open={detailOpen}
                    onRequestListRefresh={() => {
                        void load({silent: true});
                    }}
                    onClose={() => {
                        setDetailOpen(false);
                        setDetailRow(null);
                    }}
                    companyId={detailRow.companyId}
                    annualId={detailRow.annualId}
                    companyName={detailRow.companyName}
                    onUpdated={(message?: string) => {
                        setSnackbar({open: true, message: message || 'Updated successfully', severity: 'success'});
                        setDetailOpen(false);
                        setDetailRow(null);
                        void load({silent: true});
                    }}
                />
            )}
        </>
    )

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
                p: '20px',
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700}>
                        Finance Statement
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <InputField size="small" label="Company Name" value={filterCompanyName} onChange={e => {
                            setPageNum(1);
                            setFilterCompanyName(e.target.value);
                        }}/>
                        <InputField size="small" label="Assessment Year" value={filterYearOfAssessment}
                                   placeholder="e.g. 2024/25"
                                   onChange={e => {
                                       setPageNum(1);
                                       setFilterYearOfAssessment(e.target.value);
                                   }}/>
                        <Button size='small'
                                variant="contained"
                                color="secondary"
                                sx={{
                                    borderRadius: theme.shape.borderRadius,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: theme.shadows[1],
                                    '&:hover': {boxShadow: theme.shadows[3]}
                                }}
                                onClick={() => setCreateOpen(true)}>
                            Add FS
                        </Button>
                    </Stack>
                </Stack>
                {error && (
                    <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, flexShrink: 0}}>{error}</Box>
                )}
                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 500}}>Company Name</TableCell>
                                <TableCell sx={{fontWeight: 500}}>Company Chinese Name</TableCell>
                                <TableCell sx={{fontWeight: 500}}>Records</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [...Array(pageSize).keys()].map((i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                </TableRow>
                            ))}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {!loading && (() => {
                                const grouped: Record<string, any[]> = {};
                                rows.filter(r => !r.flow || r.flow === 'Audit').forEach(r => {
                                    const key = r.companyName || `unknown-${r.companyId}`;
                                    if (!grouped[key]) grouped[key] = [];
                                    grouped[key].push(r);
                                });
                                Object.values(grouped).forEach(list => list.sort((a, b) => {
                                    const ya = a.yearOfAssessment || '';
                                    const yb = b.yearOfAssessment || '';
                                    return yb.localeCompare(ya);
                                }));
                                return Object.entries(grouped).map(([companyName, annuals]) => {
                                    const isExpanded = expandedCompany === companyName;
                                    const first = annuals[0];
                                    return (
                                        <React.Fragment key={companyName}>
                                            <TableRow
                                                hover
                                                sx={{cursor: 'pointer'}}
                                                onClick={() => setExpandedCompany(isExpanded ? null : companyName)}
                                            >
                                                <TableCell>{companyName}</TableCell>
                                                <TableCell>{first?.companyChineseName || '-'}</TableCell>
                                                <TableCell sx={{color: 'text.secondary', fontSize: 12}}>
                                                    {annuals.length} record(s)
                                                </TableCell>
                                            </TableRow>
                                            {isExpanded && (
                                                <TableRow>
                                                    <TableCell colSpan={3} sx={{bgcolor: 'background.default', p: 0}}>
                                                        <Box sx={{borderTop: `1px solid ${theme.palette.divider}`, pl: '80px'}}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell sx={{fontWeight: 600}}>Year Of Assessment</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Period To</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Workflow</TableCell>
                                                                        <TableCell sx={{fontWeight: 600}}>Action</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {annuals.map((r, idx) => (
                                                                        <TableRow key={String(r.annualId ?? idx)} hover>
                                                                            <TableCell>{r.yearOfAssessment}</TableCell>
                                                                            <TableCell>{r.periodTo}</TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="caption" sx={{
                                                                                    px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                                                                                    bgcolor: r.flow === 'Audit' ? 'primary.light' : 'success.light',
                                                                                    color: r.flow === 'Audit' ? 'primary.dark' : 'success.dark',
                                                                                }}>{r.flow || 'Audit'}</Typography>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Stack direction="row" spacing={1}>
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="contained"
                                                                                        color="secondary"
                                                                                        sx={{
                                                                                            borderRadius: theme.shape.borderRadius,
                                                                                            textTransform: 'none',
                                                                                            fontWeight: 600,
                                                                                            boxShadow: theme.shadows[1],
                                                                                            '&:hover': {boxShadow: theme.shadows[3]}
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setDetailRow(r);
                                                                                            setDetailOpen(true);
                                                                                        }}>
                                                                                        Detail
                                                                                    </Button>
                                                                                </Stack>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                });
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={Math.max(0, Math.min(Math.ceil(total / pageSize) - 1, pageNum - 1))}
                    onPageChange={(_, newPage) => setPageNum(newPage + 1)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) => {
                        const next = parseInt(e.target.value, 10) || 25;
                        setPageSize(next);
                        setPageNum(1);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{mt: 1, flexShrink: 0}}
                />
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message || ''}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({...s, open: false}))}
                />

                {dialogs}
            </Box>
        </Slide>
    );
}
